import { RevoluteJoint, type Body, type World } from "fast-2d";
import type { Demo } from "./types";
import { Camera } from "../render/Camera";
import { ball, box, floor, makeWorld, walls } from "./helpers";

/** Pin a local point on `a` to a local point on `b` with a free-spinning hinge. */
function link(world: World, a: Body, aLocal: [number, number], b: Body, bLocal: [number, number]): void {
	world.addConstraint(
		new RevoluteJoint(a, b, { localAnchorA: aLocal, localAnchorB: bLocal }),
	);
}

/**
 * Build a jointed ragdoll centered at (cx, cy). Limbs are hinged with revolute
 * joints; `collideConnected` defaults off so adjacent parts don't fight.
 * Remember: −y is up, +y is down.
 */
function makeRagdoll(world: World, cx: number, cy: number): void {
	const d = 1.1; // limb density so the doll has some heft to throw
	const torso = box(world, cx, cy, 0.62, 1.05, { density: d, friction: 0.6 });
	const head = ball(world, cx, cy - 1.05 / 2 - 0.36, 0.32, { density: d });
	// Arms (hang at the shoulders).
	const uArmL = box(world, cx - 0.5, cy - 0.35, 0.55, 0.2, { density: d });
	const lArmL = box(world, cx - 1.0, cy - 0.35, 0.5, 0.18, { density: d });
	const uArmR = box(world, cx + 0.5, cy - 0.35, 0.55, 0.2, { density: d });
	const lArmR = box(world, cx + 1.0, cy - 0.35, 0.5, 0.18, { density: d });
	// Legs.
	const uLegL = box(world, cx - 0.17, cy + 0.95, 0.24, 0.68, { density: d });
	const lLegL = box(world, cx - 0.17, cy + 1.6, 0.21, 0.6, { density: d });
	const uLegR = box(world, cx + 0.17, cy + 0.95, 0.24, 0.68, { density: d });
	const lLegR = box(world, cx + 0.17, cy + 1.6, 0.21, 0.6, { density: d });

	// Neck.
	link(world, torso, [0, -0.5], head, [0, 0.34]);
	// Shoulders → upper arms → elbows → lower arms.
	link(world, torso, [-0.31, -0.45], uArmL, [0.27, 0]);
	link(world, uArmL, [-0.27, 0], lArmL, [0.25, 0]);
	link(world, torso, [0.31, -0.45], uArmR, [-0.27, 0]);
	link(world, uArmR, [0.27, 0], lArmR, [-0.25, 0]);
	// Hips → upper legs → knees → lower legs.
	link(world, torso, [-0.17, 0.5], uLegL, [0, -0.34]);
	link(world, uLegL, [0, 0.34], lLegL, [0, -0.3]);
	link(world, torso, [0.17, 0.5], uLegR, [0, -0.34]);
	link(world, uLegR, [0, 0.34], lLegR, [0, -0.3]);
}

export const ragdoll: Demo = {
	id: "ragdoll",
	title: "Ragdoll",
	blurb:
		"Jointed figures built from revolute hinges. Grab a hand or a foot and fling them — the limbs follow through every chain of joints.",
	category: "Joints & Machines",
	build() {
		const world = makeWorld();
		floor(world, 60);
		walls(world, 9, 18);

		// A couple of static perches for the dolls to drape over.
		box(world, -3.5, -3, 3, 0.4, { isStatic: true, angle: 0.18 });
		box(world, 3.5, -1.5, 3, 0.4, { isStatic: true, angle: -0.18 });

		makeRagdoll(world, -3, -7);
		makeRagdoll(world, 3, -8);

		return {
			world,
			camera: new Camera(0, -4, 26),
			hint: "Grab a limb and fling the ragdoll",
		};
	},
};
